/**
 * Folding a single changed ticket into a cached queue page.
 *
 * Pulled out of `useSupportQueue` because this is the one piece of the caching
 * work that can be *wrong* rather than merely slow: deciding client-side
 * whether a ticket belongs in a view is the same mistake the old browser-side
 * `breached` filter made, which silently returned the wrong answer as soon as
 * the queue exceeded one page.
 *
 * So the contract is deliberately narrow — splice only when membership is
 * beyond doubt, and report `handled: false` for everything else so the caller
 * falls back to asking the server.
 */

/**
 * Views where a brand-new ticket unambiguously belongs. A ticket that has just
 * been created is open and unassigned, which is exactly what `pool` and
 * `all_open` mean; every other view depends on a predicate (breached, mine,
 * escalated-to-me, snoozed) that only the server can evaluate.
 */
const NEW_TICKET_VIEWS = new Set(["pool", "all_open"]);

/**
 * Decide how a changed ticket affects the rows currently on screen.
 *
 * @param {Array<Object>} rows - Normalized tickets in the cached page.
 * @param {Object} ticket - The freshly-read normalized ticket.
 * @param {{ view: string, eventType?: string|null }} context
 * @returns {{ rows: Array<Object>, handled: boolean }} `handled: false` means
 *   the caller should revalidate instead of trusting this result.
 */
export const mergeTicketIntoRows = (rows, ticket, { view, eventType = null }) => {
  const current = Array.isArray(rows) ? rows : [];
  if (!ticket?.roomId) return { rows: current, handled: false };

  const index = current.findIndex((t) => String(t.roomId) === String(ticket.roomId));

  if (index !== -1) {
    // Already on screen: replacing it in place is always safe — the row is
    // there either way, and this is the whole point of the optimisation.
    const next = [...current];
    next[index] = ticket;
    return { rows: next, handled: true };
  }

  if (eventType === "TICKET_CREATED" && NEW_TICKET_VIEWS.has(view)) {
    // Newest-first is what an agent watching the pool expects; `sort` still
    // owns the real order on the next revalidate.
    return { rows: [ticket, ...current], handled: true };
  }

  // Not ours to place — a resolve, an assignment to someone else, a breach.
  // Let the server say.
  return { rows: current, handled: false };
};
